import { useSearch } from "@tanstack/react-router"
import { useCallback, useMemo } from "react"
import type { Client, CombinedError } from "urql"

import type { Instruction, InstructionSortField, SortingOrder } from "@/graphql/codegen/graphql"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import {
  type InstructionSearchFilters,
  useInstructionSearchStore,
} from "@/screens/ManageProducts/stores/useInstructionSearchStore"

interface UseSearchListReturn {
  searchParams: {
    query: string
    field: InstructionSortField
    order: SortingOrder
    isActive: boolean
  }
  data: Instruction[]
  fetching: boolean
  error: CombinedError | Error | undefined
  executeSearch: (client: Client, filters: InstructionSearchFilters, skipFilterCheck?: boolean) => Promise<void>
  refetchData: (skipCheck?: boolean) => Promise<void>
  clearStore: () => void
}

const useSearchList = (): UseSearchListReturn => {
  const currentSearchRaw = useSearch({ from: "/app/admin/manage-products/_layout/instructions" })
  const { query, field, order, isActive } = currentSearchRaw

  const executeSearch = useInstructionSearchStore(({ executeSearch }) => executeSearch)
  const data = useInstructionSearchStore(({ data }) => data)
  const fetching = useInstructionSearchStore(({ fetching }) => fetching)
  const error = useInstructionSearchStore(({ error }) => error)
  const clearStore = useInstructionSearchStore(({ clearStore }) => clearStore)

  const stableFilters = useMemo(
    () => ({
      query,
      field,
      order,
      isActive,
    }),
    [query, field, order, isActive]
  )

  const refetchData = useCallback(
    (skipCheck = true) => {
      return executeSearch(queryClient, stableFilters, skipCheck)
    },
    [executeSearch, stableFilters]
  )

  return {
    searchParams: {
      query,
      field,
      order,
      isActive,
    },
    data,
    fetching,
    error,
    executeSearch,
    refetchData,
    clearStore,
  }
}

export default useSearchList
